import { __ } from '@wordpress/i18n';
import { SelectControl, RangeControl, ToggleControl, FormTokenField, Notice } from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';

import PanelBody from '../../../../components/PanelBody';
import SpecificUsersControl from './SpecificUsersControl';

const SOURCE_OPTIONS = [
  { value: 'manual', label: __('Manual (add avatars)', 'brandy-blocks') },
  { value: 'users', label: __('Users by role', 'brandy-blocks') },
  { value: 'authors', label: __('Post authors', 'brandy-blocks') },
  { value: 'customers', label: __('WooCommerce customers', 'brandy-blocks') },
  { value: 'specific', label: __('Specific users', 'brandy-blocks') },
];

const COMMON_ROLES = ['administrator', 'editor', 'author', 'contributor', 'subscriber', 'customer'];

/**
 * Source controls: choose manual vs a dynamic user source, and configure the
 * matching query. Mirrors AvatarGroup::query_source_users() on the server.
 */
export default function SourcePanel({ attributes, setAttributes }) {
  const { source, query = {}, userIds = [], linkAvatars } = attributes;
  const setQuery = (patch) => setAttributes({ query: { ...query, ...patch } });

  const postTypes = useSelect((select) => {
    const raw = select(coreStore).getPostTypes({ per_page: -1 });
    const list = raw ? Object.values(raw) : [];
    return list.filter((t) => t.viewable && t.slug !== 'attachment');
  }, []);

  const isDynamic = source !== 'manual';

  return (
    <PanelBody title={__('Source', 'brandy-blocks')} initialOpen>
      <SelectControl
        label={__('Populate from', 'brandy-blocks')}
        value={source || 'manual'}
        options={SOURCE_OPTIONS}
        onChange={(value) => setAttributes({ source: value })}
      />

      {source === 'users' && (
        <FormTokenField
          label={__('Roles', 'brandy-blocks')}
          value={query.roles || []}
          suggestions={COMMON_ROLES}
          onChange={(roles) => setQuery({ roles: roles.map((r) => String(r).toLowerCase()) })}
          __experimentalExpandOnFocus
        />
      )}

      {source === 'authors' && (
        <SelectControl
          label={__('Post type', 'brandy-blocks')}
          value={query.postType || 'post'}
          options={postTypes.map((t) => ({ value: t.slug, label: t.name }))}
          onChange={(postType) => setQuery({ postType })}
        />
      )}

      {source === 'customers' && (
        <Notice status="info" isDismissible={false}>
          {__('Requires WooCommerce. Shows users with the Customer role.', 'brandy-blocks')}
        </Notice>
      )}

      {source === 'specific' && (
        <SpecificUsersControl userIds={userIds} onChange={(ids) => setAttributes({ userIds: ids })} />
      )}

      {isDynamic && source !== 'specific' && (
        <>
          <RangeControl
            label={__('Number of avatars', 'brandy-blocks')}
            value={query.number || 8}
            min={1}
            max={50}
            onChange={(number) => setQuery({ number })}
          />
          <SelectControl
            label={__('Order', 'brandy-blocks')}
            value={query.order || 'asc'}
            options={[
              { value: 'asc', label: __('Name A→Z', 'brandy-blocks') },
              { value: 'desc', label: __('Name Z→A', 'brandy-blocks') },
            ]}
            onChange={(order) => setQuery({ order })}
          />
        </>
      )}

      {source === 'authors' && (
        <ToggleControl
          label={__('Link avatars to author archive', 'brandy-blocks')}
          checked={!!linkAvatars}
          onChange={(value) => setAttributes({ linkAvatars: value })}
        />
      )}
    </PanelBody>
  );
}
